import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categoryHref } from '../../utils/categoryParam';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'sonner';
import {
  Search, Plus, Minus, ChevronRight, ChevronDown, User, ArrowRight,
  Home, Grid3X3, ShoppingCart, Package, CalendarCheck
} from 'lucide-react';
import axios from 'axios';
import { PromoBannerCarousel } from '../../components/customer/PromoBannerCarousel';
import { CouponStrip } from '../../components/customer/CouponStrip';
import { PromotionStrip } from '../../components/customer/PromotionStrip';
import { usePromotions } from '../../hooks/usePromotions';
import { UnavailableBadge, isUnavailable, isVariantUnavailable } from '../../components/customer/UnavailableBadge';
import { ComingSoonButton } from '../../components/customer/ComingSoonButton';
import { isComingSoon } from '../../utils/comingSoon';
import { ProductImageCarousel } from '../../components/customer/ProductImageCarousel';
import { SubCategoryFilter } from '../../components/customer/SubCategoryFilter';
import { VariantPicker } from '../../components/customer/VariantPicker';
import { CustomerDesktopNav } from '../../components/customer/CustomerDesktopNav';
import { CATEGORY_ICON_GRID, CUSTOMER_PAGE, PRODUCT_GRID } from '../../components/customer/customerLayout';
import { isNavItemActive, useCustomerNavItems } from '../../components/customer/customerNav';
import { ScrollRow } from '../../components/customer/ScrollRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEARCH_HINTS = ['"oil"', '"milk"', '"paneer"', '"rice"', '"vegetables"', '"fruits"'];

const CountrySearch = () => {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIdx(i => (i + 1) % SEARCH_HINTS.length); setFade(true); }, 200);
    }, 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="text-gray-400 text-sm pointer-events-none transition-all duration-200"
      style={{ opacity: fade ? 1 : 0 }}>
      search for {SEARCH_HINTS[idx]}
    </span>
  );
};

const CountryHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, storeSettings } = useAuth();
  const { addItem, getItemQuantity, updateQuantity, totalItems, totalAmount } = useCart();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [promoBanners, setPromoBanners] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const promotions = usePromotions();
  const searchRef = useRef(null);

  const sessionId = useRef(
    sessionStorage.getItem('_sid') ||
    (() => { const s = Math.random().toString(36).slice(2); sessionStorage.setItem('_sid', s); return s; })()
  );

  useEffect(() => { loadData(); trackPageview(); }, []);

  const loadData = async () => {
    try {
      const reqs = [
        axios.get(`${API}/admin/categories`),
        axios.get(`${API}/customer/products`),
      ];
      if (user?.admin_id) {
        reqs.push(axios.get(`${API}/store/${user.admin_id}/promo-banners`).catch(() => ({ data: [] })));
        reqs.push(axios.get(`${API}/store/${user.admin_id}/coupons`).catch(() => ({ data: [] })));
      }
      const results = await Promise.all(reqs);
      setCategories(results[0].data);
      setProducts(results[1].data);
      if (results[2]) setPromoBanners(results[2].data);
      if (results[3]) setCoupons(results[3].data);
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  };

  const trackPageview = () => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/pageview`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, page: '/home', source: 'direct',
    }).catch(() => {});
  };

  const handleAdd = (product, selectedVariant = null) => {
    if (!user) {
      navigate('/login');
      toast.info('Please login to add items to cart');
      return;
    }
    addItem(product, 1, selectedVariant);
    const label = selectedVariant ? `${product.name} (${selectedVariant.label})` : product.name;
    toast.success(`Added ${label}`);
  };

  const curr = storeSettings?.currency_symbol || '₹';
  const autoPlay = storeSettings?.auto_image_carousel !== false;
  const searchedProducts = searchQuery.trim()
    ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  // Split into categories for carousel sections
  const catProductMap = {};
  products.forEach(p => {
    if (!catProductMap[p.category_id]) catProductMap[p.category_id] = [];
    catProductMap[p.category_id].push(p);
  });

  // Get first populated category for "Popular" section
  const popularCat = categories.find(c => (catProductMap[c.id] || []).length > 0);
  const popularProducts = popularCat ? catProductMap[popularCat.id].slice(0, 12) : products.slice(0, 12);
  const popularLabel = popularCat ? `Popular ${popularCat.name}` : 'Popular Products';

  return (
    <div className="min-h-screen bg-white pb-24 lg:pb-10" data-testid="country-home">
      {/* ===== HEADER ===== */}
      <header className="bg-white sticky top-0 z-50 border-b border-gray-100" data-testid="country-header">
        <div className={`${CUSTOMER_PAGE} px-4 py-3 flex items-center gap-3`}>
          {/* Profile */}
          <Link to="/profile" className="lg:hidden w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center shrink-0"
            data-testid="country-profile">
            <User className="w-5 h-5 text-gray-400" />
          </Link>
          {/* Search */}
          <div className={`flex-1 bg-white border rounded-lg px-3 py-2.5 flex items-center gap-2 transition-all ${searchFocused ? 'border-[var(--theme-primary)] ring-1 ring-[var(--theme-primary)]/20' : 'border-gray-300'}`}
            onClick={() => searchRef.current?.focus()} data-testid="country-search">
            <div className="flex-1 relative">
              {/* Input stays in flow so it defines this wrapper's height — see ZeptoHome. */}
              {!searchFocused && !searchQuery && (
                <div className="absolute inset-0 flex items-center pointer-events-none">
                  <CountrySearch />
                </div>
              )}
              <input ref={searchRef} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)} onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                className="w-full bg-transparent outline-none text-sm text-[#1A1A1A]"
                data-testid="country-search-input" />
            </div>
            <Search className="w-5 h-5 text-gray-400 shrink-0" />
          </div>
          {/* The store's cart badge stays; the rest of the navigation joins it on desktop */}
          <CustomerDesktopNav showCart={false} />
          {/* Cart badge */}
          <Link to="/cart" className="bg-[var(--theme-accent-red,#EF4444)] text-white rounded-lg px-3 py-2 flex items-center gap-1.5 shrink-0"
            data-testid="country-cart-badge">
            <ShoppingCart className="w-4 h-4" />
            <span className="text-xs font-bold">{curr}{totalAmount.toFixed(0)}</span>
          </Link>
        </div>
      </header>

      <div className={CUSTOMER_PAGE}>
        {/* ===== SEARCH RESULTS ===== */}
        {searchedProducts && (
          <div className="px-4 py-4" data-testid="country-search-results">
            <p className="text-xs text-gray-500 mb-3">
              {searchedProducts.length} result{searchedProducts.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
            <div className={PRODUCT_GRID}>
              {searchedProducts.slice(0, 8).map(p => (
                <CountryProductGrid key={p.id} product={p} curr={curr}
                  getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                  onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
              ))}
            </div>
            {searchedProducts.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">No products found</p>}
          </div>
        )}

        {/* ===== MAIN CONTENT ===== */}
        {!searchedProducts && (
          <>
            {/* Admin Promo Banners */}
            <div className="pt-3">
              <PromoBannerCarousel banners={promoBanners} variant="country" />
            </div>

            {/* Coupons */}
            {/* Promotions (automatic discounts) */}
            <PromotionStrip promotions={promotions} variant="country" />

            <CouponStrip coupons={coupons} variant="country" currencySymbol={curr} />

            {/* POPULAR SECTION - Horizontal scroll */}
            {popularProducts.length > 0 && (
              <div className="pt-4 mb-2" data-testid="country-popular-section">
                <div className="flex items-center justify-between px-4 mb-3">
                  <h2 className="font-heading font-black text-xl text-[#1A1A1A]">{popularLabel}</h2>
                  <Link to="/products" className="text-sm font-semibold text-[var(--theme-primary)] flex items-center gap-0.5"
                    data-testid="country-view-more">
                    View More <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <ScrollRow className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-4 snap-x scroll-px-4" testId="country-popular-scroll">
                  {popularProducts.map(p => (
                    <CountryProductCarousel key={p.id} product={p} curr={curr}
                      getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                      onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                  ))}
                </ScrollRow>
              </div>
            )}

            {/* SHOP BY CATEGORY - 4 column grid */}
            {categories.length > 0 && (
              <div className="px-4 mb-6" data-testid="country-shop-by-category">
                <h2 className="font-heading font-black text-xl text-[#1A1A1A] mb-4">Shop By Category</h2>
                <div className={CATEGORY_ICON_GRID}>
                  {categories.slice(0, 12).map(cat => (
                    // Opens /products with this category selected (it previously
                    // ignored the tile tapped). The category is in the URL, so
                    // Back from a product returns to it; ?tenant= is kept.
                    <Link key={cat.id} to={categoryHref('/products', location.search, cat.id)} className="country-cat-item"
                      data-testid={`country-cat-${cat.id}`}>
                      <div className="cat-circle">
                        {cat.image_url ? (
                          <img src={cat.image_url} alt={cat.name} />
                        ) : (
                          <span className="text-xl font-bold text-[var(--theme-primary)]">{cat.name.charAt(0)}</span>
                        )}
                      </div>
                      <span className="text-[11px] font-semibold text-[#1A1A1A] text-center leading-tight line-clamp-2">
                        {cat.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* MORE PRODUCT SECTIONS per category */}
            {categories.slice(1, 4).map(cat => {
              const catProds = catProductMap[cat.id] || [];
              if (catProds.length === 0) return null;
              return (
                <div key={cat.id} className="mb-2" data-testid={`country-section-${cat.id}`}>
                  <div className="flex items-center justify-between px-4 mb-3">
                    <h2 className="font-heading font-black text-xl text-[#1A1A1A]">{cat.name}</h2>
                    <Link to="/products" className="text-sm font-semibold text-[var(--theme-primary)] flex items-center gap-0.5">
                      View More <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <ScrollRow className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-4 snap-x scroll-px-4">
                    {catProds.slice(0, 10).map(p => (
                      <CountryProductCarousel key={p.id} product={p} curr={curr}
                        getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                        onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                    ))}
                  </ScrollRow>
                </div>
              );
            })}

            {/* ALL PRODUCTS GRID */}
            <div className="px-4 pb-6" data-testid="country-all-products">
              <h2 className="font-heading font-black text-xl text-[#1A1A1A] mb-3">All Products</h2>
              {loading ? (
                <div className={PRODUCT_GRID}>{[1,2,3,4].map(i => <div key={i} className="h-56 bg-gray-100 rounded-xl animate-pulse" />)}</div>
              ) : (
                <div className={PRODUCT_GRID}>
                  {products.slice(0, 20).map(p => (
                    <CountryProductGrid key={p.id} product={p} curr={curr}
                      getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                      onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Reserves scroll space for the floating cart bar below (which is `fixed` and
          so doesn't occupy flow height on its own) — without this the last product
          card ends up partially hidden behind it. */}
      {totalItems > 0 && <div className="h-16 lg:hidden" aria-hidden="true" />}

      {/* ===== FLOATING CART BAR ===== */}
      {totalItems > 0 && (
        <div className="lg:hidden fixed left-0 right-0 z-40 px-4 pb-2" style={{ bottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' }} data-testid="country-floating-cart">
          <div className="max-w-3xl mx-auto">
            <Link to="/cart" className="block">
              <div className="bg-[var(--theme-primary)] text-white rounded-xl px-4 py-3 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-semibold text-sm">
                    {totalItems} item{totalItems > 1 ? 's' : ''} in cart
                  </span>
                </div>
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* ===== BOTTOM NAV ===== */}
      <CountryBottomNav />
    </div>
  );
};

/* ======================================== */
/*  Product Card - Carousel (Country)       */
/* ======================================== */
const CountryProductCarousel = ({ product, curr, getItemQuantity, onAdd, onAddBase, onUpdate, storeSettings, autoPlay }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);
  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const quantity = hasVariants ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0) : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="country-product-card" data-testid={`country-carousel-${product.id}`}>
      {discount > 0 && <div className="absolute top-2 left-2 z-10 bg-[var(--theme-primary)] text-white text-[10px] font-bold px-2 py-0.5 rounded">{discount}% OFF</div>}
      <div className="w-full aspect-square rounded-t-xl bg-[#F5F0E8] relative flex items-center justify-center overflow-hidden">
        <ProductImageCarousel product={product} autoPlay={autoPlay} />
        {productOos && !comingSoon && <UnavailableBadge />}
        {comingSoon && <ComingSoonButton size="sm" className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10" testId={`coming-soon-c-${product.id}`} />}
        {!productOos && !comingSoon && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
          {quantity === 0 ? (
            <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()}
              disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
              className="country-add-btn" data-testid={`country-add-c-${product.id}`}>ADD</button>
          ) : (
            <div className="country-qty-ctrl">
              <button onClick={() => onUpdate(cartKey, quantity - 1)}>-</button>
              <span>{quantity}</span>
              <button onClick={() => onUpdate(cartKey, quantity + 1)}>+</button>
            </div>
          )}
        </div>
        )}
      </div>
      <div className="px-2 pt-2 pb-3">
        <h4 className="text-xs font-bold text-[#1A1A1A] line-clamp-2 leading-tight">{product.name}</h4>
        {product.description && <p className="text-[10px] text-gray-400 line-clamp-1 mt-0.5">{product.description}</p>}
        <div className="flex items-center gap-1 mt-0.5">
          <p className="text-[10px] text-gray-400">{activeVariant ? activeVariant.label : product.unit}</p>
          {hasVariants && !productOos && !comingSoon && (
            <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
              className="flex items-center text-[9px] font-semibold text-[var(--theme-primary)]"
              data-testid={`change-variant-${product.id}`}>
              Change <ChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
          variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
          currencySymbol={curr} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
        <div className="mt-1.5">
          {discount > 0 && <p className="text-[10px] text-gray-400 line-through">{curr}{displayMrp}</p>}
          <p className="text-sm font-bold text-[#1A1A1A]">{curr}{displayPrice}</p>
        </div>
      </div>
    </div>
  );
};

const CountryProductGrid = ({ product, curr, getItemQuantity, onAdd, onAddBase, onUpdate, storeSettings, autoPlay }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);
  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const quantity = hasVariants ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0) : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden relative" data-testid={`country-grid-${product.id}`}>
      {discount > 0 && <div className="absolute top-2 left-2 z-10 bg-[var(--theme-primary)] text-white text-[10px] font-bold px-2 py-0.5 rounded">{discount}% OFF</div>}
      <div className="aspect-square bg-[#F5F0E8] relative flex items-center justify-center overflow-hidden">
        <ProductImageCarousel product={product} autoPlay={autoPlay} />
        {productOos && !comingSoon && <UnavailableBadge />}
        {comingSoon && <ComingSoonButton size="sm" className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10" testId={`coming-soon-g-${product.id}`} />}
        {!productOos && !comingSoon && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
          {quantity === 0 ? (
            <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()}
              disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
              className="country-add-btn" data-testid={`country-add-g-${product.id}`}>ADD</button>
          ) : (
            <div className="country-qty-ctrl">
              <button onClick={() => onUpdate(cartKey, quantity - 1)}>-</button>
              <span>{quantity}</span>
              <button onClick={() => onUpdate(cartKey, quantity + 1)}>+</button>
            </div>
          )}
        </div>
        )}
      </div>
      <div className="p-2.5">
        <h4 className="text-xs font-bold text-[#1A1A1A] line-clamp-2 leading-tight">{product.name}</h4>
        {product.description && <p className="text-[10px] text-gray-400 line-clamp-1 mt-0.5">{product.description}</p>}
        <div className="flex items-center gap-1 mt-0.5">
          <p className="text-[10px] text-gray-400">{activeVariant ? activeVariant.label : product.unit}</p>
          {hasVariants && !productOos && !comingSoon && (
            <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
              className="flex items-center text-[9px] font-semibold text-[var(--theme-primary)]"
              data-testid={`change-variant-${product.id}`}>
              Change <ChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
          variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
          currencySymbol={curr} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
        <div className="mt-1.5">
          {discount > 0 && <p className="text-[10px] text-gray-400 line-through">{curr}{displayMrp}</p>}
          <p className="text-sm font-bold text-[#1A1A1A]">{curr}{displayPrice}</p>
        </div>
      </div>
    </div>
  );
};

/* ======================================== */
/*  Bottom Nav (Country Delight)            */
/* ======================================== */
const CountryBottomNav = () => {
  const loc = typeof window !== 'undefined' ? window.location.pathname : '/';
  const items = useCustomerNavItems();

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="country-bottom-nav">
      <div className="max-w-3xl mx-auto flex items-center justify-around py-2">
        {items.map(item => {
          const active = isNavItemActive(item.path, loc);
          return (
            <Link key={item.path} to={item.path}
              className="flex flex-col items-center gap-0.5 px-3 py-1 relative"
              data-testid={`country-bnav-${item.label.toLowerCase()}`}>
              <item.icon className={`w-5 h-5 ${active ? 'text-[var(--theme-primary)]' : 'text-gray-400'}`} />
              <span className={`text-[10px] font-medium ${active ? 'text-[var(--theme-primary)]' : 'text-gray-400'}`}>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default CountryHome;
